import React, { useEffect, useState, useMemo, memo } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { auth } from '../api';
import '../styles/app-shell.css';
import '../styles/sg-ui.css';

const I = {
  home: 'M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM9 22V12h6v10',
  book: 'M4 19.5A2.5 2.5 0 0 1 6.5 17H20M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z',
  schema: 'M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11',
  mail: 'M4 4h16v16H4zM22 6l-10 7L2 6',
  shield: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10zM9 12l2 2 4-4',
  guide: 'M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2zM22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z',
  card: 'M2 5h20v14H2zM2 10h20',
  api: 'M18 16l4-4-4-4M6 8l-4 4 4 4M14.5 4l-5 16',
  message: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z',
  user: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2',
  logout: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9',
};
const Icon = ({ d }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>
);

const Layout = memo(function Layout() {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const path = useMemo(() => location.pathname, [location.pathname]);

  useEffect(() => { setOpen(false); }, [path]);

  useEffect(() => {
    (async () => {
      try {
        const res = await auth.getMe();
        setCompany(res.data);
      } catch (err) {
        localStorage.removeItem('token');
        navigate('/login');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const handleLogout = () => { localStorage.removeItem('token'); navigate('/login'); };

  if (loading) {
    return (
      <div className="sgapp">
        <aside className="sg-sidebar">
          <div className="sg-sb-brand"><img src="/project_logo.png" alt="SkillGate" /><span className="nm">SkillGate</span></div>
        </aside>
        <main className="sg-main" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center', color: 'var(--ink-500)' }}>
            <div style={{ width: 44, height: 44, border: '3px solid var(--line)', borderTopColor: 'var(--blue-600)', borderRadius: '50%', margin: '0 auto 14px', animation: 'sgspin 0.8s linear infinite' }} />
            Загрузка…
          </div>
          <style>{'@keyframes sgspin{to{transform:rotate(360deg)}}'}</style>
        </main>
      </div>
    );
  }

  const isAdmin = company?.email?.toLowerCase() === 'sergei.vikhliaev@gmail.com';
  const initials = (company?.name || 'S').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

  const main = [
    { to: '/dashboard', icon: I.home, label: 'Главная' },
    { to: '/question-banks', icon: I.book, label: 'Базы вопросов' },
    { to: '/test-schemas', icon: I.schema, label: 'Тест-схемы' },
    { to: '/invitations', icon: I.mail, label: 'Приглашения' },
    ...(isAdmin ? [{ to: '/admin', icon: I.shield, label: 'Админ панель' }] : []),
  ];
  const secondary = [
    { to: '/instructions', icon: I.guide, label: 'Инструкция' },
    { to: '/payment', icon: I.card, label: 'Оплата' },
    { to: '/api', icon: I.api, label: 'API' },
    { to: '/feedback', icon: I.message, label: 'Обратная связь' },
  ];
  const isActive = (to) => path === to || path.startsWith(to + '/');

  const navLink = (it) => (
    <Link key={it.to} to={it.to} className={isActive(it.to) ? 'active' : ''}>
      <Icon d={it.icon} /><span>{it.label}</span>
    </Link>
  );

  return (
    <div className="sgapp">
      {/* mobile top bar */}
      <div className="sg-mtop">
        <div className="b"><img src="/project_logo.png" alt="SkillGate" /><span className="nm">SkillGate</span></div>
        <button aria-label="Меню" onClick={() => setOpen(!open)}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {open ? <path d="M6 18 18 6M6 6l12 12" /> : <path d="M4 6h16M4 12h16M4 18h16" />}
          </svg>
        </button>
      </div>
      <div className={`sg-overlay ${open ? 'show' : ''}`} onClick={() => setOpen(false)} />

      <aside className={`sg-sidebar ${open ? 'open' : ''}`}>
        <div className="sg-sb-brand"><img src="/project_logo.png" alt="SkillGate" /><span className="nm">SkillGate</span></div>

        <Link to="/profile" className="sg-sb-company">
          <span className="av">{initials}</span>
          <div>
            <div className="cn">{company?.name || 'Компания'}</div>
            <div className="cl">{company?.email || 'Аккаунт'}</div>
          </div>
        </Link>

        <nav className="sg-nav">
          {main.map(navLink)}
          <div className="sg-nav-sep" />
          {secondary.map(navLink)}
        </nav>

        <div className="sg-sb-foot">
          <div className="sg-sb-legal">
            <Link to="/privacy-policy">Политика обработки данных</Link>
            <Link to="/terms-of-service">Пользовательское соглашение</Link>
            <Link to="/public-offer">Публичная оферта</Link>
          </div>
          <div className="sg-sb-org">ИП Вихляев Сергей Николаевич · ИНН 682004283576</div>
          <button className="sg-logout" onClick={handleLogout}>
            <Icon d={I.logout} />Выйти
          </button>
        </div>
      </aside>

      <main className="sg-main"><div className="sg-fade" key={path}><Outlet /></div></main>
    </div>
  );
});

export default Layout;
